import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import api from "../services/api";
import PromoBannerView from "../admin/components/PromoBannerView";

const STORAGE_KEY = "dismissedPromoBanners";

const readDismissed = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
};

/**
 * Drop this into the storefront layout:
 *   <StorefrontPromoBanner placement="TopBar" />   above the header
 *   <StorefrontPromoBanner placement="Hero" />     top of the home page
 * Shows nothing when there are no live banners.
 */
const StorefrontPromoBanner = ({ placement = "TopBar", rotateMs = 6000 }) => {
  const [banners, setBanners] = useState([]);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/api/promotions/banners/active", { params: { placement } })
      .then((res) => {
        if (!cancelled) setBanners(res.data.banners || []);
      })
      .catch((error) => {
        console.error("Error loading storefront promotion banners:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [placement]);

  const visible = banners.filter((b) => !dismissed.includes(b.BannerID));
  const isPopup = placement === "Popup";
  const current = visible.length ? visible[index % visible.length] : null;

  const dismiss = useCallback((bannerId) => {
    const next = [...dismissed, bannerId];
    setDismissed(next);
    setIndex(0);
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore storage errors */
    }
  }, [dismissed]);
  const closeCurrentPopup = useCallback(() => {
    if (!current) return;
    if (current.IsDismissible) {
      dismiss(current.BannerID);
    } else {
      setPopupOpen(false);
    }
  }, [current, dismiss]);

  useEffect(() => {
    if (!isPopup || visible.length === 0) {
      setPopupOpen(false);
      return undefined;
    }
    const timer = setTimeout(() => setPopupOpen(true), 1200);
    return () => clearTimeout(timer);
  }, [isPopup, visible.length]);

  useEffect(() => {
    if (visible.length < 2 || paused) return undefined;
    const id = setInterval(
      () => setIndex((i) => (i + 1) % visible.length),
      rotateMs,
    );
    return () => clearInterval(id);
  }, [visible.length, paused, rotateMs]);

  useEffect(() => {
    if (!isPopup || !popupOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeCurrentPopup();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isPopup, popupOpen, closeCurrentPopup]);

  if (visible.length === 0) return null;

  if (isPopup) {
    if (!popupOpen) return null;
    const onBackdropClick = (event) => {
      if (event.target === event.currentTarget) {
        closeCurrentPopup();
      }
    };
    return createPortal(
      <div
        className="pbv-popup-backdrop"
        onClick={onBackdropClick}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
      >
        <section
          className="pbv-popup-dialog"
          role="dialog"
          aria-modal="true"
          aria-label={current.Headline || current.Promotion?.Name || "Promotion"}
        >
          <PromoBannerView
            key={current.BannerID}
            banner={current}
            promotion={current.Promotion}
            onDismiss={closeCurrentPopup}
          />
          {visible.length > 1 && (
            <>
              <button
                type="button"
                className="pbv-popup-arrow previous"
                aria-label="Previous promotion"
                onClick={() =>
                  setIndex((i) => (i - 1 + visible.length) % visible.length)
                }
              >
                <FiChevronLeft />
              </button>
              <button
                type="button"
                className="pbv-popup-arrow next"
                aria-label="Next promotion"
                onClick={() => setIndex((i) => (i + 1) % visible.length)}
              >
                <FiChevronRight />
              </button>
              <div className="pbv-popup-dots" aria-label="Promotion slides">
                {visible.map((banner, dotIndex) => (
                  <button
                    key={banner.BannerID}
                    type="button"
                    className={`pbv-popup-dot ${dotIndex === index % visible.length ? "active" : ""}`}
                    aria-label={`Show promotion ${dotIndex + 1}`}
                    aria-current={dotIndex === index % visible.length}
                    onClick={() => setIndex(dotIndex)}
                  />
                ))}
              </div>
            </>
          )}
        </section>
      </div>,
      document.body,
    );
  }

  return (
    <div
      className="pbv-rotator"
      aria-live="polite"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <PromoBannerView
        key={current.BannerID}
        banner={current}
        promotion={current.Promotion}
        onDismiss={
          current.IsDismissible ? () => dismiss(current.BannerID) : undefined
        }
      />
      {placement === "Hero" && visible.length > 1 && (
        <div className="pbv-dots">
          {visible.map((b, i) => (
            <button
              key={b.BannerID}
              type="button"
              className={`pbv-dot ${i === index % visible.length ? "active" : ""}`}
              aria-label={`Show banner ${i + 1}`}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default StorefrontPromoBanner;
